/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */
import * as Blockly from 'blockly/core';
import 'blockly/python';
import { getAllowedOutputs } from '../block_compatibility_checks';
const { getCurrentSatUsingRootBlock } = require('../dataset');
import { getIndexOfValueInDropdownOptions, hasSatChanged, updateBlockSat } from '../helper_functions';

const premadeChartList = { //eslint-disable-line no-unused-vars
    'landCover': [
        ['Bar Chart', 'bar'],
        ['Horizontal Bar Chart', 'horizontalBar'],
        ['Doughnut Chart', 'doughnut'],
        ['Pie Chart', 'pie'],
        ['Radar Chart', 'radar'],
        ['Line Chart', 'line']
    ],  
    'ice': [
        ['Bar Chart', 'bar'],
        ['Doughnut Chart', 'doughnut'],
        ['Pie Chart', 'pie'],
        ['Radar Chart', 'radar'],
        ['Stacked Bar Chart', 'stacked bar'],
        ['Horizontal Bar Chart', 'horizontalBar'],
        ['Stacked Horizontal Bar Chart', 'stacked horizontalBar']
    ],
    'comparison': [
        ['Bar Chart', 'bar'],
        ['Stacked Bar Chart', 'stacked bar'],
        ['Horizontal Bar Chart', 'horizontalBar'],
        ['Stacked Horizontal Bar Chart', 'stacked horizontalBar']
    ],
    'fire': [
        ['Bar Chart', 'bar'],
        ['Line Chart', 'line'],
        ['Stacked Bar Chart', 'stacked bar'],
        ['Horizontal Bar Chart', 'horizontalBar'],
        ['Stacked Horizontal Bar Chart', 'stacked horizontalBar']
    ],
    'annual_burned_area': [
        ['Bar Chart', 'bar'],
        ['Stacked Bar Chart', 'stacked bar'],
        ['Horizontal Bar Chart', 'horizontalBar'],
        ['Stacked Horizontal Bar Chart', 'stacked horizontalBar']
    ],
    'else': [
        ['Bar Chart', 'bar'],
        ['Line Chart', 'line'],
        ['Doughnut Chart', 'doughnut'],
        ['Pie Chart', 'pie'],
        ['Radar Chart', 'radar'],
        ['Stacked Bar Chart', 'stacked bar'],
        ['Area Line Chart', 'line area'],
        ['Horizontal Bar Chart', 'horizontalBar'],
        ['Stacked Horizontal Bar Chart', 'stacked horizontalBar'],
        ['Histogram', 'histogram']
    ],
    'none': [
        ['None', 'none']
    ]
}

const satToCompatibleChartList = {
    'copernicus_cover': premadeChartList['landCover'],
    'corine': premadeChartList['landCover'],
    'world_cover': premadeChartList['landCover'],
    'croplands': premadeChartList['landCover'],
    'firms': premadeChartList['fire'],
    'terra_fires': premadeChartList['fire'],
    'annual_burned_area': premadeChartList['annual_burned_area'],
    'hansen': [['Bar Chart', 'bar']],
    'hansen_cover': [['Bar Chart', 'bar']],
    'hansen_fnf': premadeChartList['none'],
    'hansen_loss': premadeChartList['none'],
    'hansen_gain': premadeChartList['none'],
    'else': premadeChartList['else'],
    'biomass_nasa_2010': [['Bar Chart', 'bar'], ['Stacked Bar Chart', 'stacked bar']],
    'analyse_change_detection': [['Histogram', 'histogram']]
}

Blockly.Blocks['output_charts'] = {
    init: function () {
        // create options based on parent when initialised
        let startOptions = this.generateChartOptions();

        this.appendDummyInput('chartInput')
            .appendField(new Blockly.FieldDropdown(startOptions), "chartType");

        this.setPreviousStatement(true, 'presentation');
        this.setNextStatement(true, getAllowedOutputs('presentation'));
        this.setStyle('dashboards');
        this.setTooltip(Blockly.Msg.CHARTS_BLOCK_TOOLTIP);
    },

    // TODO create on change as an abstracted helper function
    onchange: function(event) {
        // adding checks for the end of a drag event i.e. a block has been moved somewhere
        // e.g. outside of its parent block, change events pick up whenever something
        // in the entire block hierarchy has changed e.g. the user changes a dropdown in the parent
        // block
        // DO NOT ALLOW event.type == 'move' THIS WILL CAUSE HEADACHES
        // event.type == 'create' is also quite janky
        // using finished_loading event to capture when user loads workflow or when a featuredWorkflow is clicked
        if((event.type == 'drag' && !event.isStart) || event.type == 'change' || event.type == 'finished_loading'){
            updateBlockSat(this);
            if (hasSatChanged(this)) {
                this.updateChartOptions();
            }
        }
    },

    generateChartOptions() {
        let rootBlock = this.getRootBlock()
        let satKeys = Object.keys(satToCompatibleChartList)

        let isSatInkeyList = (sat) => satKeys.includes(sat)
        if(rootBlock === null) return satToCompatibleChartList['else']

        let sat = getCurrentSatUsingRootBlock(rootBlock);
        if(isSatInkeyList(sat)) return satToCompatibleChartList[sat]
        
        return satToCompatibleChartList['else'];
    },

    updateChartOptions(){
        let oldChartValue = this.getFieldValue("chartType");

        this.removeInput('chartInput');

        let options = this.generateChartOptions();

        // add field back to bllock
        this.appendDummyInput('chartInput')
            .appendField(new Blockly.FieldDropdown(options), "chartType");

        // check if old value is in new options list, if so use it, otherwise use first option of old block
        let newChartValue = getIndexOfValueInDropdownOptions(oldChartValue, options) === -1 ? options[0][1] : oldChartValue;

        // update field with value
        this.setFieldValue(newChartValue, 'chartType')
    }
}